import { State, Action, StateContext, Selector } from '@ngxs/store';
import {
  GetAllGeneralFilterAction,
  UpdateFilterStateAction,
  ClearFilterStateAction,
  UpdateSelectedFiltersAction,
  GetAllClientsFilterAction,
  UpdateClientsFiltersAction,
  GetAllCityGatesFilterAction,
  UpdateSelectedGatesFilterAction,
  UpdateClientIdMedidor,
  UpdateAllGeneralFilterAction,
  UpdateCreg185FilterAction,
  UpdateClientTypeFilterAction,
} from './general-filter.actions';
import { Injectable } from '@angular/core';
import { HttpService } from '@/app/core/services/http/http.service';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { CONSUMPTION_FILTER_TYPES } from '@/app/core/const/consumption.const';
import { IComplexFilter } from '@/app/core/interfaces/prediction.interface';

export interface IFilterInfo {
  selectedFilters: string[];
  selectedClient: string | null;
  selectedFiltersGates: string[];
  filterClients: string | any;
  filterPoints: string | any;
  medidorId: number | null;
  creg185: boolean | null;
  clientType: string | null;
}

export interface GeneralFilterStateModel {
  generalInfo: IComplexFilter[];
  clientsInfo: any[];
  cityGates: any[];
  filters: { [filterId: string]: IFilterInfo };
  [key: string]: any;
}

@State<GeneralFilterStateModel>({
  name: 'GeneralFilter',
  defaults: {
    generalInfo: [],
    clientsInfo: [],
    cityGates: [],
    filters: {},
  },
})
@Injectable()
export class GeneralFilterState {
  @Selector() static GetAllData(
    state: GeneralFilterStateModel
  ): IComplexFilter[] {
    return state.generalInfo ?? [];
  }

  @Selector() static GetAllDataClients(
    state: GeneralFilterStateModel
  ): IComplexFilter[] {
    return state.clientsInfo ?? [];
  }

  @Selector() static GetFilterState(state: GeneralFilterStateModel): {
    [filterId: string]: IFilterInfo;
  } {
    return state.filters;
  }

  @Selector() static GetAllDataCityGates(
    state: GeneralFilterStateModel
  ): any[] {
    return state.cityGates ?? [];
  }

  /**
   * Busca el UUID de un punto de salida (city gate) por su nombre
   */
  static GetCityGateUuidByName(nombre: string) {
    return (state: GeneralFilterStateModel) => {
      const cityGates = state.cityGates ?? [];
      const cityGate = cityGates.find(
        (gate: any) =>
          gate.nombre?.toLowerCase().trim() === nombre?.toLowerCase().trim()
      );
      return cityGate?.uuid || null;
    };
  }

  @Selector() static GetMedidorId(
    state: GeneralFilterStateModel,
    filterId: string
  ) {
    return state.filters[filterId].medidorId;
  }

  constructor(
    private httpService: HttpService,
    public dialog: MatDialog
  ) {}

  @Action(GetAllGeneralFilterAction)
  async GetAllGeneralFilterAction(ctx: StateContext<GeneralFilterStateModel>) {
    try {
      const resp = await this.httpService.getGeneralFilter();
      if (resp) {
        const options: any[] = [];
        Object.keys(resp).forEach((key: string) => {
          options.push({
            filterLabel: CONSUMPTION_FILTER_TYPES[key],
            filterInfo: resp[key],
            filterName: key,
          });
        });
        ctx.patchState({
          generalInfo: options,
        });
      }
    } catch (error) {
      console.error('Error en la llamada al servicio:', error);
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(GetAllClientsFilterAction)
  async GetAllClientsFilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: GetAllClientsFilterAction
  ) {
    const state = ctx.getState();
    const filterId = action.filterId;
    const filterState: IFilterInfo = state.filters[filterId] || {
      selectedFilters: [],
      selectedClient: null,
      selectedFiltersGates: [],
      filterClients: null,
      filterPoints: null,
      medidorId: null,
      creg185: null,
      clientType: null,
    };
    const selectedFilters = filterState.selectedFiltersGates || [];
    const generalFilters = filterState.selectedFilters || [];
    const clientType = filterState.clientType;
    const creg185 = filterState.creg185;

    try {
      const filterParts: string[] = [];

      // Agregar filtros generales
      if (generalFilters.length > 0) {
        filterParts.push(...generalFilters);
      }

      // Agregar filtros de gates
      if (selectedFilters.length > 0) {
        filterParts.push(...selectedFilters);
      }

      // Agregar clientType si está seleccionado
      if (clientType) {
        filterParts.push(`${clientType}=true`);
      }

      // Agregar creg185 si está seleccionado
      if (creg185 === true) {
        filterParts.push(`creg185=${creg185}`);
      }

      const actualFilter = filterParts.join('&');

      const resp: any = await this.httpService.getClientsFilter(actualFilter);
      if (resp) {
        ctx.patchState({
          clientsInfo: resp[0].clientes,
        });
      }
    } catch (error) {
      console.error('Error en la llamada al servicio:', error);
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          hiddeCancelBtn: true,
          clientsInfoLoaded: true,
        },
      });
    }
  }

  @Action(GetAllCityGatesFilterAction)
  async GetAllCityGatesFilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: GetAllCityGatesFilterAction
  ) {
    const state = ctx.getState();
    const filterId = action.filterId;
    const filterState: IFilterInfo = state.filters[filterId] || {
      selectedFilters: [],
      selectedClient: null,
      selectedFiltersGates: [],
      filterClients: null,
      filterPoints: null,
      medidorId: null,
      creg185: null,
      clientType: null,
    };
    const selectedFilters = filterState.selectedFilters || [];
    const clientType = filterState.clientType;
    const creg185 = filterState.creg185;

    try {
      const filterParts: string[] = [];

      // Agregar filtros generales
      if (selectedFilters.length > 0) {
        filterParts.push(...selectedFilters);
      }

      // Agregar clientType si está seleccionado
      if (clientType) {
        filterParts.push(`${clientType}=true`);
      }

      // Agregar creg185 si está seleccionado
      if (creg185 === true) {
        filterParts.push(`creg185=${creg185}`);
      }

      const actualFilter = filterParts.join('&');

      const resp = await this.httpService.getCityGates(actualFilter);

      if (resp) {
        ctx.patchState({
          cityGates: resp,
        });
      }
    } catch (error) {
      console.error('Error en la llamada al servicio:', error);
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          hiddeCancelBtn: true,
          cityGatesLoaded: true,
        },
      });
    }
  }

  @Action(UpdateFilterStateAction)
  UpdateFilterStateAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateFilterStateAction
  ) {
    ctx.patchState({
      ...action.payload,
    });
  }

  @Action(UpdateAllGeneralFilterAction)
  UpdateAllGeneralFilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateAllGeneralFilterAction
  ) {
    const value = action.payload;
    if (Array.isArray(value)) {
      ctx.patchState({
        generalInfo: value,
      });
    } else {
      console.error('El payload proporcionado no es un array válido');
    }
  }

  @Action(UpdateSelectedFiltersAction)
  UpdateSelectedFiltersAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateSelectedFiltersAction
  ) {
    const state = ctx.getState();
    const { selectedFilters, filterId } = action;

    const updatedFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        selectedFilters: selectedFilters,
      },
    };

    ctx.patchState({
      filters: updatedFilters,
    });
  }

  @Action(UpdateSelectedGatesFilterAction)
  async UpdateSelectedGatesFilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateSelectedGatesFilterAction
  ) {
    const state = ctx.getState();
    const { selectedFiltersGates, filterId } = action;
    const updatedFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        selectedFiltersGates: selectedFiltersGates,
      },
    };
    ctx.patchState({
      filters: updatedFilters,
    });
  }

  @Action(UpdateClientsFiltersAction)
  async UpdateClientsFiltersAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateClientsFiltersAction
  ) {
    const state = ctx.getState();
    const { selectedClient, filterId } = action;

    const updatedFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        selectedClient: selectedClient,
      },
    };
    ctx.patchState({
      filters: updatedFilters,
    });
  }

  @Action(UpdateClientIdMedidor)
  UpdateClientIdMedidor(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateClientIdMedidor
  ) {
    const state = ctx.getState();
    const { clientIdMedidor, filterId } = action;

    const updatedFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        medidorId: clientIdMedidor,
      },
    };
    ctx.patchState({
      filters: updatedFilters,
    });
  }

  @Action(UpdateCreg185FilterAction)
  UpdateCreg185FilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateCreg185FilterAction
  ) {
    const state = ctx.getState();
    const { filterId, creg185 } = action;

    const updateFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        creg185: creg185,
      },
    };

    ctx.patchState({
      filters: updateFilters,
    });
  }

  @Action(UpdateClientTypeFilterAction)
  UpdateClientTypeFilterAction(
    ctx: StateContext<GeneralFilterStateModel>,
    action: UpdateClientTypeFilterAction
  ) {
    const state = ctx.getState();
    const { filterId, clientType } = action;

    const updateFilters = {
      ...state.filters,
      [filterId]: {
        ...state.filters[filterId],
        clientType: clientType,
      },
    };

    ctx.patchState({
      filters: updateFilters,
    });
  }
}
